import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GhostUpdateCombinationService } from './ghost-update-combination.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-ghost-update-combination',
    templateUrl: './ghost-update-combination.component.html',
    styleUrls: ['./ghost-update-combination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GhostUpdateCombinationComponent implements OnInit, OnDestroy {

  /**
   * Output channels kept identical to the AngularJS bridge in
   * `ghostUpdateCombinationCtrl.js`, which does:
   *   - `close-ghostUpdateCombinationconfirm-info` -> `$uibModalInstance.close()`
   *   - `save-ghostUpdateCombinationsync-info`     -> `$rootScope.$emit('gridUpdate')` + close
   *
   * These DOM events also propagate to the AngularJS shell when this CE is
   * mounted under a $uibModal template.
   */
  @Output('close-ghostUpdateCombinationconfirm-info')
  closefirTestedUnitsconfirmInfo = new EventEmitter<string>();

  @Output('save-ghostUpdateCombinationsync-info')
  savefirTestedUnitsSyncInfo = new EventEmitter<string>();

  /**
   * Standard CE-host bridge event consumed by `CeHostDialog` so that this
   * confirmation modal can also be opened from the migrated grid.directive
   * `handleFormCallback` flow (i.e. when there is no AngularJS uibModal
   * wrapper around it).
   */
  @Output('request-grid-update')
  requestGridUpdate = new EventEmitter<string>();

  showModal: boolean = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to update Settlement Redirection?',
    iconClass: 'Appkit4-icon icon-logout-fill',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  baseGlobalParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '/gtw', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  clob_settings: {
    tax_year: number;
    scenario: number;
    jcd_key: number;
    parent_me_string: string;
    ho_leid_opt: string;
    filing_key: number;
  } = {
    tax_year: 0,
    scenario: 0,
    jcd_key: 0,
    parent_me_string: '',
    ho_leid_opt: '',
    filing_key: 0
  };
  clob_data: any = [];

  private ssoId = '';
  private checkLockedObj: any = [];
  private filterParams: any = {};
  private modalName = '';
  private saveRequestSeq = 0;

  subscriptions: Subscription = new Subscription();

  constructor(
    private GhostUpdateCombinationService: GhostUpdateCombinationService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) {}

  checkInputType(): boolean {
    if (Number.isInteger(parseInt(this.ssoId + ''))) {
      return true;
    }
    return false;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.checkLockedObj = this.baseGlobalParams?.checkLockedbj || this.checkLockedObj;
    this.modalName = this.baseGlobalParams?.modalScreen?.modal_name || this.modalName;
    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.ssoId = sso != null && sso !== '' ? String(sso) : this.ssoId;
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.checkInputType()) {
      // No-op; placeholder retained for parity with AngularJS controller.
    }
  }

  onCancel(): void {
    this.showModal = false;
    this.closefirTestedUnitsconfirmInfo.emit('close-ghostUpdateCombinationconfirm-info');
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.markForCheck();

    const processName = 'RUN_SYNC_TESTED_UNITS';

    this.clob_settings = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key,
      parent_me_string: this.filterParams.me_string == undefined ? null as any : this.filterParams.me_string,
      ho_leid_opt: this.filterParams.ho_leid_opt == undefined ? null as any : this.filterParams.ho_leid_opt,
      filing_key: this.filterParams.filing_key
    };

    const tempData = {
      TAX_YEAR: this.filterParams?.tax_year,
      SCENARIO: this.filterParams?.scenario,
      JCD_KEY: this.baseGlobalParams?.jcd_key,
      SSO_ID: this.ssoId,
      FILING_KEY: this.filterParams.filing_key
    };

    const seq = ++this.saveRequestSeq;
    const updateSubscription = this.GhostUpdateCombinationService.synctestedUnits(
      this.baseURLs.api,
      tempData,
      this.clob_settings,
      this.clob_data,
      processName,
      this.baseGlobalParams,
      this.checkLockedObj
    ).subscribe(
      (data: any) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        if (data.callSuccess == '1') {
          this.GhostUpdateCombinationService.openSnackBar(
            'Settlement Redirection has been successfully updated.',
            'gtw-snackbar--green'
          );
          this.savefirTestedUnitsSyncInfo.emit('save-ghostUpdateCombinationsync-info');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.GhostUpdateCombinationService.openSnackBar(
            'Error updating combination key. ' + data.errorMessage,
            'gtw-snackbar--red'
          );
          this.modalConfig = { ...this.modalConfig, loading: false };
          this.closefirTestedUnitsconfirmInfo.emit('close-ghostUpdateCombinationconfirm-info');
          this.requestGridUpdate.emit('cancel');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        if (seq !== this.saveRequestSeq) {
          return;
        }
        this.GhostUpdateCombinationService.openSnackBar(
          'Error updating combination key. ' + error?.error?.errorMessage,
          'gtw-snackbar--red'
        );
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.markForCheck();
      }
    );

    this.subscriptions.add(updateSubscription);
  }
}
